import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Badge,
} from "reactstrap";
import { getEmployeesWithDetails } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const documentTypes = [
  { value: "all", label: "All Documents" },
  { value: "passport", label: "Passport" },
  { value: "visa", label: "Visa" },
  { value: "nationalId", label: "Nationality ID" },
  { value: "labourCard", label: "Labour Card" },
];

const emptyFilter = {
  business_Id: null,
  branch_Id: null,
  department_Id: null,
  employee_Id: null,
  documentType: "all",
  fromDate: null,
  toDate: null,
};

export default function ExpiringDocumentsPage() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filterApplied, setFilterApplied] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsExpiringDocuments');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate company in filter if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !filter.business_Id) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setFilter(prev => ({
          ...prev,
          business_Id: session.businessId
        }));
        // Load branches for the selected business
        loadBranches(session.businessId);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch in filter if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && filter.business_Id && !filter.branch_Id) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setFilter(prev => ({
          ...prev,
          branch_Id: session.branchId
        }));
      }
    }
  }, [branches, session.branchId, filter.business_Id]);

  // Remove automatic filtering - only filter when Apply Filter is clicked
  useEffect(() => {
    if (!filterApplied && employees.length > 0) {
      processEmployeesData();
    }
  }, [employees]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadEmployees(),
        loadBusinesses(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const employeeFilter = {};
      // Use filter state if available, otherwise use session data
      if (filter.business_Id) {
        employeeFilter.employee_Business_Id = filter.business_Id;
      } else if (session.businessId) {
        employeeFilter.employee_Business_Id = session.businessId;
      }
      if (filter.branch_Id) {
        employeeFilter.employee_Branch_Id = filter.branch_Id;
      } else if (session.branchId && session.businessId) {
        employeeFilter.employee_Branch_Id = session.branchId;
      }

      const data = await getEmployeesWithDetails(employeeFilter);
      setEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
      throw error;
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({ business_Active: true });
      const data = Array.isArray(response) ? response : (response.data || []);
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
      throw error;
    }
  };

  const loadBranches = async (businessId) => {
    try {
      if (!businessId) {
        setBranches([]);
        return;
      }
      const response = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchData = Array.isArray(response) ? response : (response.data || []);
      const branchOptions = branchData.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
      throw error;
    }
  };

  const loadDepartments = async (branchId) => {
    try {
      const response = await getDepartments({ Branch_Id: branchId });
      const data = Array.isArray(response) ? response : (response.data || []);
      const departmentOptions = data.map(d => ({
        value: d.department_Id,
        label: d.department_NameEn || d.department_Name
      }));
      setDepartments(departmentOptions);
    } catch (error) {
      console.error("Error loading departments:", error);
      throw error;
    }
  };

  const processEmployeesData = () => {
    const today = new Date();
    const expiringDocuments = [];

    employees.forEach(employee => {
      // Check each document type for expiry
      const documents = [];

      // Passport
      if (employee.employee_Passport_ExpDate) {
        const expiryDate = new Date(employee.employee_Passport_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Passport",
          number: employee.employee_PassportNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      // Visa
      if (employee.employee_Visa_ExpDate) {
        const expiryDate = new Date(employee.employee_Visa_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Visa",
          number: employee.employee_VisaNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      // National ID
      if (employee.employee_NatID_ExpDate) {
        const expiryDate = new Date(employee.employee_NatID_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Nationality ID",
          number: employee.employee_NatID || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      // Labour Card
      if (employee.employee_LabourCardExpiry) {
        const expiryDate = new Date(employee.employee_LabourCardExpiry);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Labour Card",
          number: employee.employee_LabourCardNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      // Add employee with documents (now includes all documents, not just expiring ones)
      if (documents.length > 0) {
        documents.forEach(doc => {
          expiringDocuments.push({
            ...employee,
            documentType: doc.type,
            documentNumber: doc.number,
            expiryDate: doc.expiryDate,
            daysLeft: doc.daysLeft,
            status: doc.status
          });
        });
      }
    });

    // Apply filters
    let filtered = expiringDocuments;

    // Show all documents by default (including expired ones)
    // No default filtering applied

    if (filter.business_Id) {
      filtered = filtered.filter(item => item.employee_Business_Id === filter.business_Id);
    }

    if (filter.branch_Id) {
      filtered = filtered.filter(item => item.employee_Branch_Id === filter.branch_Id);
    }

    if (filter.department_Id) {
      filtered = filtered.filter(item => item.employee_Department_ID === filter.department_Id);
    }

    if (filter.employee_Id) {
      filtered = filtered.filter(item => item.employee_Id === filter.employee_Id);
    }

    if (filter.documentType !== "all") {
      const docTypeMap = {
        passport: "Passport",
        visa: "Visa",
        nationalId: "Nationality ID",
        labourCard: "Labour Card"
      };
      filtered = filtered.filter(item => item.documentType === docTypeMap[filter.documentType]);
    }

    // Apply date filters if provided
    if (filter.fromDate) {
      filtered = filtered.filter(item => item.expiryDate >= filter.fromDate);
    }

    if (filter.toDate) {
      filtered = filtered.filter(item => item.expiryDate <= filter.toDate);
    }

    // Sort by days left (ascending)
    filtered.sort((a, b) => a.daysLeft - b.daysLeft);

    setFilteredEmployees(filtered);
    setTotalCount(filtered.length);
    setPage(1); // Reset to first page when filters change
    setFilterApplied(true); // Mark that filters have been applied
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessChange = async (selected) => {
    setFilter(prev => ({
      ...prev,
      business_Id: selected ? selected.value : null,
      branch_Id: null,
      department_Id: null,
      employee_Id: null
    }));

    if (selected) {
      try {
        await loadBranches(selected.value);
      } catch (error) {
        console.error("Error loading branches:", error);
      }
    } else {
      setBranches([]);
      setDepartments([]);
    }
  };

  const handleBranchChange = async (selected) => {
    setFilter(prev => ({
      ...prev,
      branch_Id: selected ? selected.value : null,
      department_Id: null,
      employee_Id: null
    }));

    if (selected) {
      try {
        await loadDepartments(selected.value);
      } catch (error) {
        console.error("Error loading departments:", error);
      }
    } else {
      setDepartments([]);
    }
  };

  const handleApplyFilter = () => {
    console.log("Applying filters:", filter);
    setFilterApplied(true);
    processEmployeesData();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setBranches([]);
    setDepartments([]);
    setFilterApplied(false); // Clear filter applied state
    // Show all documents (including expired ones)

    const expiringDocuments = [];

    employees.forEach(employee => {
      const documents = [];

      if (employee.employee_Passport_ExpDate) {
        const expiryDate = new Date(employee.employee_Passport_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Passport",
          number: employee.employee_PassportNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      if (employee.employee_Visa_ExpDate) {
        const expiryDate = new Date(employee.employee_Visa_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Visa",
          number: employee.employee_VisaNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      if (employee.employee_NatID_ExpDate) {
        const expiryDate = new Date(employee.employee_NatID_ExpDate);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Nationality ID",
          number: employee.employee_NatID || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      if (employee.employee_LabourCardExpiry) {
        const expiryDate = new Date(employee.employee_LabourCardExpiry);
        const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        documents.push({
          type: "Labour Card",
          number: employee.employee_LabourCardNumber || "N/A",
          expiryDate: expiryDate,
          daysLeft: daysLeft,
          status: daysLeft < 0 ? "expired" : (daysLeft <= 10 ? "critical" : "warning")
        });
      }

      if (documents.length > 0) {
        documents.forEach(doc => {
          expiringDocuments.push({
            ...employee,
            documentType: doc.type,
            documentNumber: doc.number,
            expiryDate: doc.expiryDate,
            daysLeft: doc.daysLeft,
            status: doc.status
          });
        });
      }
    });

    // Show all documents (including expired ones)
    let filtered = expiringDocuments;

    // Sort by days left (ascending)
    filtered.sort((a, b) => a.daysLeft - b.daysLeft);

    setFilteredEmployees(filtered);
    setTotalCount(filtered.length);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedData = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredEmployees.slice(startIndex, endIndex);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadge = (status) => {
    if (status === "expired") {
      return <Badge color="dark">Expired</Badge>;
    } else if (status === "critical") {
      return <Badge color="danger">Critical</Badge>;
    } else {
      return <Badge color="warning">Warning</Badge>;
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="GMS" breadcrumbItem="Expiring Documents" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">
                    <i className="bx bx-calendar-exclamation me-2"></i>
                    Expiring Documents
                  </h4>
                  <Button
                    color="outline-primary"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <i className={`bx ${showFilters ? 'bx-chevron-up' : 'bx-filter-alt'} me-1`}></i>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </Button>
                </div>

                {/* Filter Section */}
                {showFilters && (
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Company</Label>
                            <Select
                              isClearable={!session.businessId}
                              isDisabled={!!session.businessId}
                              value={businesses.find(b => b.value === filter.business_Id) || null}
                              onChange={handleBusinessChange}
                              options={businesses}
                              placeholder="Select Company"
                              classNamePrefix="select2-selection"
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Branch</Label>
                            <Select
                              isClearable={!session.branchId}
                              isDisabled={!!session.branchId || !filter.business_Id}
                              value={branches.find(b => b.value === filter.branch_Id) || null}
                              onChange={handleBranchChange}
                              options={branches}
                              placeholder="Select Branch"
                              classNamePrefix="select2-selection"
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Department</Label>
                            <Select
                              isClearable
                              value={departments.find(d => d.value === filter.department_Id) || null}
                              onChange={(selected) => handleFilterChange("department_Id", selected ? selected.value : null)}
                              options={departments}
                              placeholder="Select Department"
                              classNamePrefix="select2-selection"
                              isDisabled={!filter.branch_Id}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>Document Type</Label>
                            <Select
                              value={documentTypes.find(d => d.value === filter.documentType)}
                              onChange={(selected) => handleFilterChange("documentType", selected.value)}
                              options={documentTypes}
                              classNamePrefix="select2-selection"
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>From Date</Label>
                            <DatePicker
                              selected={filter.fromDate}
                              onChange={(date) => handleFilterChange("fromDate", date)}
                              className="form-control"
                              placeholderText="Select From Date"
                              dateFormat="dd-MM-yyyy"
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label>To Date</Label>
                            <DatePicker
                              selected={filter.toDate}
                              onChange={(date) => handleFilterChange("toDate", date)}
                              className="form-control"
                              placeholderText="Select To Date"
                              dateFormat="dd-MM-yyyy"
                            />
                          </div>
                        </Col>
                      </Row>
                      <div className="d-flex gap-2">
                        <Button color="primary" onClick={handleApplyFilter}>
                          <i className="bx bx-search me-1"></i>
                          Apply Filter
                        </Button>
                        <Button color="warning" onClick={handleClearFilter}>
                          <i className="bx bx-reset me-1"></i>
                          Clear Filter
                        </Button>
                      </div>
                      {filterApplied && (
                        <div className="mt-2">
                          <Badge color="info">
                            <i className="bx bx-filter-alt me-1"></i>
                            Filters Applied
                          </Badge>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      <i className="bx bx-list-ul me-2"></i>
                      Expiring Documents List ({totalCount} records)
                      {filterApplied && filter.fromDate && filter.toDate && (
                        <small className="text-muted ms-2">
                          (Filtered: {filter.fromDate.toLocaleDateString()} - {filter.toDate.toLocaleDateString()})
                        </small>
                      )}
                      {!filterApplied && (
                        <small className="text-muted ms-2">
                          (Showing documents expiring in next 30 days)
                        </small>
                      )}
                    </h5>

                    {loading ? (
                      <div className="text-center py-4">
                        <ClipLoader size={50} color="#556ee6" />
                        <p className="mt-2">Loading expiring documents...</p>
                      </div>
                    ) : (
                      <>
                         <div className="table-responsive">
                           <Table bordered hover>
                             <thead className="table-light">
                               <tr>
                                 <th>Sr.No</th>
                                 <th>Code</th>
                                 <th>Name</th>
                                 <th>Branch</th>
                                 <th>Department</th>
                                 <th>Designation</th>
                                 <th>Document</th>
                                 <th>Number</th>
                                 <th>Date</th>
                                 <th>Days</th>
                               </tr>
                             </thead>
                             <tbody>
                               {getPaginatedData().length === 0 ? (
                                 <tr>
                                   <td colSpan="10" className="text-center py-4">
                                     <i className="bx bx-info-circle me-2"></i>
                                     {filterApplied
                                       ? "No documents found matching the selected filters"
                                       : "No documents expiring in the next 30 days"}
                                   </td>
                                 </tr>
                               ) : (
                                 getPaginatedData().map((item, index) => (
                                   <tr key={`${item.employee_Id}-${item.documentType}-${index}`}>
                                     <td>{(page - 1) * pageSize + index + 1}</td>
                                     <td>{item.employee_Code}</td>
                                     <td>{item.employee_NameEn}</td>
                                     <td>{item.branch_name}</td>
                                     <td>{item.department_name}</td>
                                     <td>{item.designation_name}</td>
                                     <td>{item.documentType}</td>
                                     <td>{item.documentNumber}</td>
                                     <td>{formatDate(item.expiryDate)}</td>
                                     <td>
                                       <span className={`badge ${item.daysLeft < 0 ? 'bg-danger' : (item.daysLeft <= 10 ? 'bg-danger' : 'bg-warning')}`}>
                                         <span className={item.daysLeft < 0 ? 'text-white fw-bold' : ''}>
                                           {item.daysLeft < 0 ? `Expired ${Math.abs(item.daysLeft)} ${Math.abs(item.daysLeft) === 1 ? 'day' : 'days'} ago` : `${item.daysLeft} ${item.daysLeft === 1 ? 'day' : 'days'} left`}
                                         </span>
                                       </span>
                                     </td>
                                   </tr>
                                 ))
                               )}
                             </tbody>
                           </Table>
                         </div>

                        {/* Pagination */}
                        {totalCount > pageSize && (
                          <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                            style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                            <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                              <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                              Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Button
                                color="light"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                  color: page === 1 ? '#6c757d' : '#495057'
                                }}
                              >
                                <i className="mdi mdi-chevron-left"></i>
                              </Button>

                              {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                const pageNum = index + 1;
                                const isActive = page === pageNum;

                                return (
                                  <Button
                                    key={pageNum}
                                    color={isActive ? "primary" : "light"}
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    style={{
                                      border: '1px solid #dee2e6',
                                      borderRadius: '0.375rem',
                                      minWidth: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: isActive ? '#556ee6' : '#fff',
                                      borderColor: isActive ? '#556ee6' : '#dee2e6',
                                      color: isActive ? '#fff' : '#495057',
                                      fontWeight: isActive ? '600' : '400',
                                      boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                    }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}

                              <Button
                                color="light"
                                size="sm"
                                disabled={page === Math.ceil(totalCount / pageSize)}
                                onClick={() => handlePageChange(page + 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                  color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                }}
                              >
                                <i className="mdi mdi-chevron-right"></i>
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 